/* eslint-disable no-console */
import {
  StyledTreeCategory,
  StyledTreeSubcategory,
  StyledTreeItem,
  StyledCheckbox,
  StyledTreeView,
} from './styles';

import { default as LayerModel } from 'services/models/Layer';
import slugify from 'services/utils/slugify';

import Icon from '@map/components/Icons';
/**
 * useState é um hook que nos permite rastrear o estado em um
 * componente de função, o estado geralmente se refere a dados ou
 * propriedads que precisam ser rastreador em um aplicativo.
 * 
 * useEffect é um hook que permite executar efeitos colaterais em
 * seus componentes, por exemplo: buscar dados, atualizar diretamente
 * o DOM e cronômetros, ele aceita dois argumentos o segundo é opcional.
 * 
 * useRef permite que persista valores entre renderizações, ele pode
 * ser usado para armazenar um valor mutável que não causa uma nova
 * renderização quando atualizado e pode ser usado para acessar di-
 * retamente um elemento do DOM.
 */
import { useState, useEffect, useRef } from 'react';
/**
 *  useMap é um hook personalizado criado para gerenciar a comunicação
 *  dos componentes com o mapa.
 */
import { useMap } from 'views/MapView/hooks';

/**
 * CustomTreeItem
 * 
 * @param {boolean} item
 * @param {function} showDetails
 * @param {function} onClick
 * @param {object} selected
 * @param {function} handleSelect
 * 
 * @returns 
 */
const CustomTreeItem = ({
  item,
  showDetails,
  onClick,
  selected,
  handleSelect,
}) => {
  if (item) {
    const nodeId = LayerModel.generateId(item);
    let layerTitle = item.getTitle() || 'Sem nome';
    return (
      <StyledTreeItem
        nodeId={nodeId}
        label={
          <div
            className="custom_tree_item layer"
            onClick={(e) => {
              e.stopPropagation();
              showDetails(e, nodeId);
              onClick && onClick(e);
            }}
          >
            <p
              onClick={(e) => {
                e.stopPropagation();
                showDetails(e, nodeId);
                onClick && onClick(e);
              }}
            >
              {layerTitle}
            </p>
            <StyledCheckbox
              checked={selected.includes(nodeId)}
              color="primary"
              onClick={(e) => {
                e.stopPropagation();
                handleSelect(e, [nodeId]);
              }}
              size="small"
            />
          </div>
        }
      >
      </StyledTreeItem>
    );
  }
};

/**
 * CustomTreeSubcategory
 * 
 * @param {number} nodeId
 * @param {string} label
 * @param {*} children
 * @param {object} selected
 * @param {object} expanded
 * @param {function} showDetails
 * @param {function} handleSelect
 * @param {function} onClick
 * @param {array} layersByCategories
 * @param {*} codinomeCategoryParent
 * @param {object} layerChidrens
 * 
 * @returns 
 */
const CustomTreeSubcategory = ({
  nodeId,
  label,
  children,
  selected,
  expanded,
  showDetails,
  handleSelect,
  onClick,
  layersByCategories,
  codinomeCategoryParent,
  layerChidrens,
}) => {
  let subcategoryFromCategory = layersByCategories.filter((lc) => {
    return lc !== null && lc.divisao_categoria_mae === codinomeCategoryParent;
  });
  return (
    <StyledTreeSubcategory
      key={nodeId}
      nodeId={nodeId}
      expanded={expanded.includes(nodeId)}
      label={
        <div className="custom_tree_item subcategory" onClick={onClick}>
          <p className="custom_tree_subcategory">{label}</p>
          <div className="expandedIcon">
            <Icon i="map-slimarrow" />
          </div>
        </div>
      }
    >
      {layerChidrens.map((chd, key) => {
        return (
          <CustomTreeItem
            key={key}
            item={chd}
            showDetails={showDetails}
            onClick={onClick}
            selected={selected}
            handleSelect={handleSelect}
          />
        );
      })}
      {subcategoryFromCategory.map((chd, key) => {
        let layersBySubcategories = [chd];
        return (
          <RenderItensFromCategory
            key={key}
            showDetails={showDetails}
            onClick={onClick}
            selected={selected}
            expanded={expanded}
            handleSelect={handleSelect}
            layersByCategories={layersBySubcategories}
            codinomeCategoryParent={codinomeCategoryParent}
            layerChidrens={[]}
          />
        );
      })}
    </StyledTreeSubcategory>
  );
};

/**
 * RenderItensFromCategory
 * 
 * @param {boolean} item
 * @param {function} showDetails
 * @param {function} onClick
 * @param {object} selected
 * @param {object} expanded
 * @param {function} handleSelect
 * @param {array} layersByCategories
 * @param {*} codinomeCategoryParent 
 * @param {object} layerChidrens
 * 
 * @returns 
 */
const RenderItensFromCategory = ({
  item,
  showDetails,
  onClick,
  selected,
  expanded,
  handleSelect,
  layersByCategories,
  codinomeCategoryParent,
  layerChidrens,
}) => {
  let listChildrens = [];
  // Obtem Subcategorias
  let subcategoryFromCategory =
    layersByCategories.length > 0
      ? layersByCategories.filter(
          (lc) => lc && lc.divisao_categoria_mae === codinomeCategoryParent
        )
      : [];
  listChildrens = [...layerChidrens, ...subcategoryFromCategory];
  return listChildrens.map((chd, key) => {
    // Se for Subcategoria, tem propriedade 'apelido'
    return 'apelido' in chd ? (
      <CustomTreeSubcategory
        key={chd.categoria_codinome}
        nodeId={chd.categoria_codinome}
        label={chd.apelido}
        selected={selected}
        expanded={expanded}
        showDetails={showDetails}
        handleSelect={handleSelect}
        layersByCategories={layersByCategories}
        codinomeCategoryParent={chd.categoria_codinome}
        layerChidrens={chd.children}
      />
    ) : (
      <CustomTreeItem
        key={key}
        item={chd}
        showDetails={showDetails}
        onClick={onClick}
        selected={selected}
        handleSelect={handleSelect}
      />
    );
  });
};

/**
 * CustomTreeCategory
 * 
 * @param {number} nodeId
 * @param {string} label
 * @param {*} children
 * @param {object} selected
 * @param {object} expanded
 * @param {function} handleSelect
 * @param {function} showDetails
 * @param {function} onClick
 * @param {array} layersByCategories
 * 
 * @returns 
 */
const CustomTreeCategory = ({
  nodeId,
  label,
  children,
  selected,
  expanded,
  handleSelect,
  showDetails,
  onClick,
  layersByCategories,
}) => {
  if (layersByCategories) {
    return layersByCategories.map(
      (category, key) =>
        category &&
        category.divisao_categoria_mae === null && (
          <StyledTreeCategory
            key={category.categoria_codinome}
            nodeId={category.categoria_codinome}
            expanded={expanded.includes(category.categoria_codinome)}
            label={
              <div className="custom_tree_item category" onClick={onClick}>
                {category.apelido}
                <div className="expandedIcon">
                  <Icon i="map-slimarrow" />
                </div>
              </div>
            }
          >
            {
              <RenderItensFromCategory
                key={key}
                showDetails={showDetails}
                onClick={onClick}
                selected={selected}
                expanded={expanded}
                handleSelect={handleSelect}
                codinomeCategoryParent={category.categoria_codinome}
                layersByCategories={layersByCategories}
                layerChidrens={category.children}
              />
            }
          </StyledTreeCategory>
        )
    );
  }
};

/**
 * default
 * 
 * @param {object} loadedLayers Carrega as camadas
 * @param {fuction} showDetails Mostra os detalhes das camadas
 * @param {*} activeNode
 * @param {boolean} callback
 * @param {string} searchQuery Responsavel por fzr a busca
 */
export default ({
  layers: loadedLayers,
  showDetails,
  activeNode,
  callback,
  searchQuery,
}) => {
  const [persistentSelected, setPersistentSelected] = useState([]);
  const [selected, setSelected] = useState([]);
  const [expanded, setExpanded] = useState([]);
  const [layers, setLayers] = useState([]);
  const initialLayers = useRef([]);

  const { layers: contextLayers } = useMap();

  const handleSelect = (event, nodeIds) => {
    setSelected(nodeIds);
  };

  const handleToggle = (event, nodeIds) => {
    setExpanded(nodeIds);
  };

  useEffect(() => {
    let tmpPersistentSelected = [...persistentSelected];
    if (selected.length) {
      selected.forEach((item) => {
        // se não for uma categoria prossegue
        if (item.includes('---')) {
          if (tmpPersistentSelected.includes(item) && selected.length < 2) {
            tmpPersistentSelected.splice(
              tmpPersistentSelected.indexOf(item),
              1
            );
          } else {
            tmpPersistentSelected.push(item);
          }
        }
      });
      setPersistentSelected(tmpPersistentSelected);
    }
    // Aqui eu não posso habilitar a dependência de persistentSelected senão daria infinite loop
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selected]);

  useEffect(() => {
    // Quando for expandir uma categoria, não é para colocar como selected
    let tmpPersistentSelected = [...persistentSelected];
    if (expanded.length) {
      expanded.forEach((item) => {
        tmpPersistentSelected.splice(tmpPersistentSelected.indexOf(item), 1);
      });
    } else {
      setPersistentSelected(tmpPersistentSelected);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [expanded]);

  useEffect(() => {
    if (!initialLayers.current.length) {
      initialLayers.current = contextLayers.reduce((acc, cur) => {
        return [...acc, LayerModel.generateId(cur)];
      }, []);
    }
  }, [layers, contextLayers]);

  useEffect(() => {
    if (callback) callback(persistentSelected);
  }, [persistentSelected, callback]);

  // ! Essas funções de formular id de camada deve ficar tudo numa classe de camadas
  // TODO Transformar módulos em classes
  useEffect(() => {
    /**
     * Método que obtém as subcategorias diretas da categoria
     */
    const getSubcategoryChildrens = (categoriaCodinome) => {
      return loadedLayers.categoria_camada.filter((cc) => {
        return cc.divisao_categoria_mae === categoriaCodinome;
      });
    };
    /**
     * Método que verifica recursivamente exitencia dos filhos
     * procurados nas subcategorias
     */
    // (item, dest) => {
    const hasGrandchild = (categoriaCodinome, searchedLayers) => {
      let subcategoryChildrens = getSubcategoryChildrens(categoriaCodinome);
      let subcategoryHasChildrenFinded = false;
      subcategoryChildrens.forEach((cc) => {
        // Verifica se na subcategoria há algum layer procurado
        searchedLayers.forEach((layer) => {
          if (layer.parentId === cc.categoria_codinome) {
            subcategoryHasChildrenFinded = true;
          }
        });
        // Se não achou nenhuma camada filho
        if (!subcategoryHasChildrenFinded) {
          //chama novamente o método
          subcategoryHasChildrenFinded = hasGrandchild(
            cc.categoria_codinome,
            searchedLayers
          );
        }
      });
      return subcategoryHasChildrenFinded;
    };

    const hasParentSearched = (categoriaCodinome, searchedCategories) => {
      let subcategoryFinded = false;
      let subcategoryChildrens = getSubcategoryChildrens(categoriaCodinome);

      if(searchedCategories.some((cat) => cat.categoria_codinome === categoriaCodinome)){
        subcategoryFinded = true;
      }

      subcategoryChildrens.forEach((cc) => {
        // Verifica se alguma subcategoria filha foi procurada
        searchedCategories.forEach((cat) => {
          if (cat.categoria_codinome === cc.categoria_codinome) {
            subcategoryFinded = true;
          }
        });
        // Se não achou nenhuma categoria filho
        if (subcategoryFinded === false) {
          //chama novamente o método
          subcategoryFinded = hasParentSearched(
            cc.categoria_codinome,
            searchedCategories
          );
        }
      });

      return subcategoryFinded;
    }

    const getChildrensTree = (categoriesFiltered, categories, layers) => {
      const codenameTopParents = [];
      const layersParents = [];
      if(categoriesFiltered.length > 0){

      categories.forEach((cat) => {
        if(cat.divisao_categoria_mae != null){
          categoriesFiltered.forEach((catFilter) => {
            if (catFilter.categoria_codinome === cat.divisao_categoria_mae) {
              codenameTopParents.push(cat.categoria_codinome);
              layers.forEach((layer) => {
                if (layer.parentId === cat.categoria_codinome) {
                  layersParents.push(layer);
                }
              });
            }          
          })
        }
      })
      if(codenameTopParents.length > 0){
        categories.forEach((cat) => {
          if(cat.divisao_categoria_mae != null){
            codenameTopParents.forEach((catParent) => {
              if (catParent === cat.divisao_categoria_mae) {
                codenameTopParents.push(cat.categoria_codinome);
                layers.forEach((layer) => {
                  if (layer.parentId === cat.categoria_codinome) {
                    layersParents.push(layer);
                  }
                });
              }          
            })
          }
        })
      }
    }
    return {
      "categories": codenameTopParents,
      "layers": layersParents,
    };

    }

    if (loadedLayers && loadedLayers.categoria_camada) {
      const regex = new RegExp(`${slugify(searchQuery)}`, 'gi') || null;
      let tmpLayersCategories = [...loadedLayers.categoria_camada].filter(
        (cat) => cat?.configuracao?.visible ?? true
      );
      tmpLayersCategories.sort((a, b) => a.ordem - b.ordem);
      // Filtrando camadas caso exista filtro de camadas
      let tmpLayers =
        searchQuery !== null
          ? loadedLayers.camadas.filter((layer) => {
              let isVisible = layer?.additionalProps?.visible ?? true;
              let layerTitle = slugify(layer.getTitle());
              return layerTitle && layerTitle.match(regex) && isVisible;
            })
          : [...loadedLayers.camadas].filter(
              (layer) => layer?.additionalProps?.visible ?? true
            );
      tmpLayers.sort((a, b) => a.order - b.order);

      let tempCategories = 
        searchQuery !== null
          ? tmpLayersCategories.filter((cat) => {
            let categoryTitle = slugify(cat.apelido);
            return categoryTitle && categoryTitle.match(regex);
          }) : [...loadedLayers.categoria_camada].filter(
            (cat) => cat?.configuracao?.visible ?? true
          );

      let layersByCategories = [];
      tmpLayersCategories.forEach((category) => {
        // Filtrando categorias caso exista filtro
        let categoryChildren = [];
        let subcategoryChildren = [];
        let categoryTitle = slugify(category.apelido);
        if (
          searchQuery !== null &&
          categoryTitle &&
          categoryTitle.match(regex)
        ) {
          categoryChildren = [
            ...loadedLayers.camadas.filter(
              (layer) => {
                if (layer?.additionalProps?.visible === false ) return false;
                return layer.parentId === category.categoria_codinome;
              }
            ),
          ];

          subcategoryChildren = [
            ...tmpLayersCategories.filter(
              (subcat) => {
                return subcat.divisao_categoria_mae === category.categoria_codinome
            }),
          ];

          categoryChildren.sort((a, b) => a.order - b.order);
        } else {
          categoryChildren = [
            ...tmpLayers.filter(
              (layer) => layer.parentId === category.categoria_codinome
            ),
          ];
          subcategoryChildren = [
            ...tmpLayersCategories.filter(
              (subcat) =>
                subcat.divisao_categoria_mae === category.categoria_codinome
            ),
          ];
        }

        layersByCategories.push({
          ...category,
          children: categoryChildren,
          subcategoryChildren: subcategoryChildren,
        });
      });

      tmpLayersCategories = tmpLayersCategories.reduce((acc, cur) => {
        return { ...acc, [cur.categoria_codinome]: cur };
      }, {});

      const childrensTree = getChildrensTree(tempCategories, layersByCategories, loadedLayers.camadas);
      // Excluindo categorias que não tenham filhos diretos e que não possuem nenhuma
      // subcategora que tenha um dos filhos procurados
      layersByCategories.forEach((category, i, a) => {
        if (
          category.children.length === 0 &&
          !hasGrandchild(category.categoria_codinome, tmpLayers) &&
          !hasParentSearched(category.categoria_codinome, tempCategories)
        ) {
          if(!childrensTree.categories.includes(category.categoria_codinome)){
            a[i] = null;
          }else{
            category.children = loadedLayers.camadas.filter((l) => category.categoria_codinome === l.parentId)
          }
        }
      });

      tmpLayers = tmpLayers.reduce((acc, cur) => {
        return { ...acc, [LayerModel.generateId(cur)]: cur };
      }, {});
      setLayers({
        categories: tmpLayersCategories,
        layers: tmpLayers,
        layersByCategories,
      });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadedLayers, searchQuery]);

  return (
    <StyledTreeView
      expanded={expanded}
      selected={selected}
      onNodeToggle={handleToggle}
      onNodeSelect={handleSelect}
      multiSelect
    >
      {layers?.categories && (
        <CustomTreeCategory
          selected={persistentSelected}
          expanded={expanded}
          handleSelect={handleSelect}
          showDetails={showDetails}
          layersByCategories={layers.layersByCategories}
        />
      )}
    </StyledTreeView>
  );
};
